import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { FeatureAccess } from "../features";
import { PageDialog } from "./ReferenceUI";

export type ExtraPage = "premium" | "invite" | "about" | "legal" | "help";
type AppInfo = {
  inviteUrl: string;
  supportUrl: string | null;
  documents: Array<{ title: string; url: string | null }>;
};
const features = [
  ["Золотой значок", "Ваша анкета ярче и заметнее"],
  ["Лайки", "Узнайте, кто поставил вам лайк"],
  ["Видео из анкет", "Смотрите видео в профилях"],
  ["Перемотка", "Вернитесь к последней просмотренной анкете"],
  ["Суперлайки", "Выделитесь среди других симпатий"],
  ["Социальные сети", "Больше способов познакомиться"],
  ["Скрыть статус онлайн", "Управляйте своей видимостью"],
  ["Скрыть мой возраст", "Выбирайте, что показывать в анкете"],
  ["Бесконечные свайпы", "Знакомьтесь без ограничения просмотров"],
];
const titles: Record<ExtraPage, string> = {
  premium: "DonDate Premium",
  invite: "Пригласить друзей",
  about: "О DonDate",
  legal: "Соглашения",
  help: "Помощь",
};
export function ProfileExtras({ page, onClose }: { page: ExtraPage; onClose(): void }) {
  const [access, setAccess] = useState<FeatureAccess | null>(null);
  const [info, setInfo] = useState<AppInfo | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [slide, setSlide] = useState(0);
  const load = useCallback(async () => {
    setError("");
    try {
      const [info, rights] = await Promise.all([
        api<AppInfo>("/v1/app-info"),
        api<FeatureAccess>("/v1/me/features").catch(() => null),
      ]);
      setInfo(info);
      setAccess(rights);
    } catch {
      setError("Не удалось загрузить информацию.");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const copy = async () => {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(info.inviteUrl);
      setStatus("Ссылка скопирована");
    } catch {
      setStatus("Скопируйте ссылку из поля ниже.");
    }
  };
  const share = async () => {
    if (!info) return;
    if (!navigator.share) {
      await copy();
      return;
    }
    try {
      await navigator.share({ title: "DonDate", text: "Знакомься в DonDate", url: info.inviteUrl });
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError"))
        setStatus("Не удалось поделиться. Скопируйте ссылку.");
    }
  };
  return (
    <PageDialog title={titles[page]} onClose={onClose}>
      {page === "premium" ? (
        <div className="reference-premium">
          <div className="reference-premium-symbol" aria-hidden="true">
            ◇
          </div>
          <h1>DonDate Premium</h1>
          <p>Дополнительные возможности</p>
          <div className="reference-feature">
            <span aria-hidden="true">{["✓", "♡", "▷", "↶", "☆", "@", "◉", "◷", "∞"][slide]}</span>
            <h2>{features[slide]?.[0]}</h2>
            <p>{features[slide]?.[1]}</p>
          </div>
          <div className="reference-carousel">
            <button type="button" aria-label="Предыдущее преимущество" onClick={() => setSlide((slide + 8) % 9)}>
              ‹
            </button>
            {features.map(([title], i) => (
              <button
                key={title}
                type="button"
                aria-label={title}
                aria-pressed={slide === i}
                onClick={() => setSlide(i)}
                className={slide === i ? "active" : ""}
              >
                •
              </button>
            ))}
            <button type="button" aria-label="Следующее преимущество" onClick={() => setSlide((slide + 1) % 9)}>
              ›
            </button>
          </div>
          <div className="reference-group">
            {features.map(([title, description], i) => (
              <button className="reference-feature-row" type="button" key={title} onClick={() => setSlide(i)}>
                <strong>{title}</strong>
                <small>{description}</small>
                <span>›</span>
              </button>
            ))}
          </div>
          {access?.features?.superlikes && (
            <p className="reference-active-plan">
              Доступно суперлайков сегодня: {access.superlikes.remaining} из {access.superlikes.limit}
            </p>
          )}
          <p className="reference-note">
            Покупка подписки готовится к запуску. Стоимость и способы оплаты появятся здесь после подключения платежей.
          </p>
        </div>
      ) : !info ? (
        <div role="status">
          {error || "Загружаем…"}
          {error && (
            <button type="button" onClick={() => void load()}>
              Повторить
            </button>
          )}
        </div>
      ) : (
        <>
          {page === "invite" && (
            <div className="reference-invite">
              <div className="reference-premium-symbol" aria-hidden="true">
                ♧
              </div>
              <h1>Пригласить друзей</h1>
              <p>Поделитесь ссылкой — возможно, ваши друзья тоже хотят познакомиться.</p>
              <div className="reference-group">
                <button className="reference-row" type="button" onClick={() => void share()}>
                  Отправить друзьям <span>›</span>
                </button>
                <button className="reference-row" type="button" onClick={() => void copy()}>
                  Копировать ссылку <span>›</span>
                </button>
              </div>
              <input
                className="reference-input"
                aria-label="Ссылка на DonDate"
                value={info.inviteUrl}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
              <p role="status">{status}</p>
              <button className="reference-primary" type="button" onClick={() => void share()}>
                Поделиться
              </button>
            </div>
          )}
          {page === "legal" && (
            <div className="reference-group reference-documents">
              {info.documents.map((doc) =>
                doc.url ? (
                  <a className="reference-row" key={doc.title} href={doc.url} target="_blank" rel="noopener noreferrer">
                    <span className="reference-row-label">{doc.title}</span>
                    <span>›</span>
                  </a>
                ) : (
                  <div className="reference-row" key={doc.title}>
                    <span className="reference-row-label">{doc.title}</span>
                    <small>Готовится</small>
                  </div>
                ),
              )}
            </div>
          )}
          {page === "about" && (
            <div className="reference-about">
              <h1>
                DonDate
                <br />
                Знакомства рядом
              </h1>
              <p>Новые знакомства, встречи и общение в вашем городе.</p>
              <div className="reference-about-art" aria-hidden="true">
                <span>Д</span>
                <span>♡</span>
              </div>
              <h2>Начните с симпатии</h2>
              <p>Посмотрите анкеты и отметьте тех, кто вам понравился. Если симпатия взаимна, откроется чат.</p>
              <h2>Комфортное общение</h2>
              <p>
                Расскажите о себе, выберите цель знакомства и найдите человека, с которым хочется продолжить разговор.
              </p>
              <h2>Безопасность и доверие</h2>
              <p>
                Фотографии проходят модерацию. В анкете и чате можно пожаловаться на нарушение или заблокировать
                пользователя.
              </p>
              <button type="button" className="reference-primary" onClick={onClose}>
                Вернуться в приложение
              </button>
            </div>
          )}
          {page === "help" && (
            <div className="reference-help">
              <h2>Частые вопросы</h2>
              {[
                [
                  "Почему анкета на проверке?",
                  "После загрузки фотографии её проверяет модератор. Одобренное главное фото необходимо для знакомства.",
                ],
                [
                  "Как начать общение?",
                  "Поставьте лайк. После взаимной симпатии в разделе «Чаты» появится совпадение.",
                ],
                [
                  "Как скрыть анкету?",
                  "Откройте настройки аккаунта в профиле и выключите «Показывать анкету». Переписки сохранятся.",
                ],
                ["Как удалить аккаунт?", "Откройте настройки аккаунта в профиле, выберите удаление и подтвердите его."],
              ].map(([q, a]) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
              {info.supportUrl ? (
                <a className="reference-primary" href={info.supportUrl} target="_blank" rel="noopener noreferrer">
                  Связаться с поддержкой
                </a>
              ) : (
                <p className="reference-note">Контакт поддержки пока не опубликован.</p>
              )}
            </div>
          )}
        </>
      )}
    </PageDialog>
  );
}
